/**
 * Three slots and nothing more (design D4).
 *
 * Pure rules: plain data in, plain data out, no renderer and no store. A refusal is a
 * `null` return rather than a thrown error — the idiom `objectives.ts` already uses —
 * because a full pocket is an ordinary outcome the store has to turn into a message, not
 * an exceptional one. Returning the same array when nothing changed is what lets the
 * store bail out without a write, and the HUD not re-render.
 */

import { isStowable } from './objectKinds';
import type { ObjectKind } from './profile/types';

/** How many items the player can carry beyond the one in their hands. */
export const POCKET_SLOTS = 3;

/**
 * What sits in a slot: the object's id, and what kind of thing it is.
 *
 * The kind travels with the id rather than being looked up from it, for two reasons. The
 * item is out of the world while it is pocketed — its body is disabled and nothing else
 * holds its kind — so a retrieval would have nowhere to read it back from; and the HUD
 * names the slot's contents, which is `OBJECT_KINDS[kind].label`.
 */
export type PocketItem = { readonly id: string; readonly kind: ObjectKind };

/** The pocket's slots, in order. `null` is an empty slot. */
export type Pocket = readonly (PocketItem | null)[];

export function emptyPocket(): Pocket {
  return new Array<PocketItem | null>(POCKET_SLOTS).fill(null);
}

/** The slot a stow would fill, or `null` when the pocket is full. */
export function firstFreeSlot(pocket: Pocket): number | null {
  const slot = pocket.findIndex((item) => item === null);
  return slot === -1 ? null : slot;
}

/**
 * Just the part of a profile the pocket rules ask about (design D3 of `per-game-pocket`).
 *
 * A whole `Profile` is assignable to it, so a caller passes the one it has straight from
 * the store; and a test of these rules states the single thing it is varying instead of
 * building a game to say it.
 */
export type PocketSettings = { readonly pocket?: boolean };

/**
 * Whether this game has a pocket at all.
 *
 * Absent means it does: the field is an opt-out for a game with nothing worth carrying
 * two of, and every profile written before it existed means what it always meant. `null`
 * is "no profile yet", which is the loading screen — answering `true` there keeps the HUD
 * from showing a pocket for one frame and taking it away again.
 */
export function hasPocket(profile: PocketSettings | null): boolean {
  return profile?.pocket ?? true;
}

/**
 * Whether a stow is on offer right now: a game with a pocket, something small in hand,
 * and somewhere to put it.
 *
 * One rule rather than two spellings of it (design D2 of `stow-key-hint`) — the touch
 * stow button appears on this answer and the desktop key hint is shown on it, so a player
 * is never taught a control on one device that the other device would refuse. The whole
 * answer, including whether the game has a pocket at all, so that a surface cannot get a
 * yes by asking only half the question. `null` for the kind is empty hands.
 */
export function canStow(
  kind: ObjectKind | null,
  pocket: Pocket,
  profile: PocketSettings | null,
): boolean {
  if (!hasPocket(profile)) return false;
  if (kind === null || !isStowable(kind)) return false;
  return firstFreeSlot(pocket) !== null;
}

/** The pocket with `item` in its lowest free slot, or `null` when there is none. */
export function stowInPocket(pocket: Pocket, item: PocketItem): Pocket | null {
  const slot = firstFreeSlot(pocket);
  if (slot === null) return null;

  const next = [...pocket];
  next[slot] = item;
  return next;
}

/**
 * The pocket without the item in `slot`, and the item. `null` when the slot is empty or
 * is not a slot — the number comes from a key press, so it is checked rather than trusted.
 */
export function takeFromSlot(
  pocket: Pocket,
  slot: number,
): { pocket: Pocket; item: PocketItem } | null {
  if (!Number.isInteger(slot) || slot < 0 || slot >= pocket.length) return null;

  const item = pocket[slot];
  if (item === null || item === undefined) return null;

  const next = [...pocket];
  next[slot] = null;
  return { pocket: next, item };
}
